import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { PageContainer, PageHeader } from "../components/PageHeader";
import EditorCard from "../components/template/EditorCard";
import { describeError, useToast } from "../components/toast";
import type { ActionKey } from "../components/toast/ToastProvider";
import {
  foyerApi,
  type IngestOverviewJob,
  type IngestOverviewView,
} from "../http/foyer";
import {
  INGEST_BUCKETS,
  INGEST_SOURCES,
  type IngestBucket,
  type IngestSource,
} from "@shared/ingest-source";
import { tokens } from "../theme";
import { useFormat } from "../i18n/format";
import { translateKey, type Translate } from "../i18n/translate";

/**
 * Ingest overview - what is running right now, and who asked for it.
 *
 * One page over every ingest source (bulk, single VOD, single MRSS item, MRSS
 * sync) because the questions an operator actually has cut across them: "why is
 * nothing moving", "what is that sync doing", "did the one I approved this
 * morning fail". Everything is read from the jobs themselves; the page keeps no
 * state of its own that could disagree with them.
 *
 * A queued job and a HELD job are shown apart on purpose. The first is waiting
 * for the farm (the stack cap, below), the second is waiting for a person - and
 * the page says which, because the two need opposite reactions.
 */

const BUCKET_TONE: Record<IngestBucket, string> = {
  queued: tokens.inkMuted,
  held: tokens.warning,
  running: tokens.brand,
  failed: tokens.error,
  completed: tokens.success,
};

const PHASES = [
  "queued",
  "transcoding",
  "ingesting",
  "publishing",
  "completed",
  "failed",
] as const;

const POLL_MS = 10_000;

function ago(t: Translate, iso: string): string {
  const k = (unit: string, count: number) =>
    translateKey(t, "content:ingestOverviewPage.ago." + unit, { count });
  const s = Math.max(
    0,
    Math.round((Date.now() - new Date(iso).getTime()) / 1000)
  );
  if (s < 90) return k("seconds", s);
  const m = Math.round(s / 60);
  if (m < 90) return k("minutes", m);
  const h = Math.round(m / 60);
  if (h < 36) return k("hours", h);
  return k("days", Math.round(h / 24));
}

function bucketOf(job: IngestOverviewJob): IngestBucket {
  if (job.phase === "completed") return "completed";
  if (job.phase === "failed") return "failed";
  if (job.phase === "queued") return job.hold ? "held" : "queued";
  return "running";
}

function detail(
  t: Translate,
  job: IngestOverviewJob,
  view: IngestOverviewView
): string {
  const k = (name: string) =>
    translateKey(t, "content:ingestOverviewPage.detail." + name);
  if (job.error) return job.error;
  if (job.hold) {
    return job.hold.reason === "awaiting_review"
      ? k("awaitingReview")
      : k("awaitingSubmission");
  }
  if (job.phase === "queued") {
    return view.readiness.ready
      ? k("waitingSlot")
      : (view.readiness.explanation ?? k("notConfigured"));
  }
  return "";
}

function Row({
  job,
  view,
  onChanged,
}: {
  job: IngestOverviewJob;
  view: IngestOverviewView;
  onChanged: () => void;
}) {
  const toast = useToast();
  const { t } = useTranslation(["content", "common"]);
  const fmt = useFormat();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const bucket = bucketOf(job);
  const jobName = job.title ?? job.assetId;

  const act = async (
    fn: () => Promise<unknown>,
    done: string,
    failed: ActionKey
  ) => {
    setBusy(true);
    setErr(null);
    try {
      await fn();
      toast.success(done);
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      toast.error(describeError(t, failed, e, { name: jobName }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <TableRow hover data-testid={`ingest-row-${job.id}`}>
      <TableCell>
        <Typography variant="body2" sx={{ fontWeight: "fontWeightSemibold" }}>
          {job.title ?? job.assetId}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {job.supplierName ??
            (job.supplierId ? job.supplierId : t("ingestOverviewPage.nenda"))}
          {job.title ? ` · ${job.assetId.slice(0, 8)}` : ""}
        </Typography>
      </TableCell>
      <TableCell>
        <Chip
          size="small"
          variant="outlined"
          label={t(`ingestOverviewPage.source.${job.source}`)}
        />
      </TableCell>
      <TableCell>
        <Typography variant="caption">
          {job.transcoder === "videocore"
            ? t("ingestOverviewPage.transcoder.videocore")
            : t("ingestOverviewPage.transcoder.encore")}
        </Typography>
      </TableCell>
      <TableCell sx={{ minWidth: 150 }}>
        <Chip
          size="small"
          variant="outlined"
          label={
            (PHASES as readonly string[]).includes(job.phase)
              ? t(
                  `ingestOverviewPage.phase.${job.phase as (typeof PHASES)[number]}`
                )
              : job.phase
          }
          sx={{ color: BUCKET_TONE[bucket], borderColor: BUCKET_TONE[bucket] }}
        />
        {job.phase === "transcoding" && job.progress != null && (
          <LinearProgress
            variant="determinate"
            value={Math.round(job.progress * 100)}
            sx={{ mt: 0.5 }}
          />
        )}
      </TableCell>
      <TableCell>
        <Tooltip title={fmt.dateTime(job.phaseStartedAt)}>
          <Typography variant="caption">
            {ago(t, job.phaseStartedAt)}
          </Typography>
        </Tooltip>
      </TableCell>
      <TableCell sx={{ maxWidth: 320 }}>
        <Typography
          variant="caption"
          color={bucket === "failed" ? "error" : "text.secondary"}
        >
          {detail(t, job, view)}
        </Typography>
        {err && (
          <Typography variant="caption" color="error" display="block">
            {err}
          </Typography>
        )}
      </TableCell>
      <TableCell align="right">
        {job.phase === "failed" && (
          <Button
            size="small"
            disabled={busy}
            onClick={() =>
              act(
                () => foyerApi.retryIngest(job.id),
                t("ingestOverviewPage.toasts.retryQueued", { name: jobName }),
                "retryIngest"
              )
            }
            data-testid={`ingest-retry-${job.id}`}
          >
            {t("ingestOverviewPage.retry")}
          </Button>
        )}
        {job.phase !== "failed" && job.phase !== "completed" && (
          <Button
            size="small"
            color="inherit"
            disabled={busy}
            onClick={() =>
              act(
                () => foyerApi.abandonIngestJob(job.id),
                t("ingestOverviewPage.toasts.abandoned", { name: jobName }),
                "abandonIngest"
              )
            }
            data-testid={`ingest-abandon-${job.id}`}
          >
            {t("ingestOverviewPage.abandon")}
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
}

function StackCap({
  view,
  onChanged,
}: {
  view: IngestOverviewView;
  onChanged: () => void;
}) {
  const stack = view.stacks[0];
  const toast = useToast();
  const { t } = useTranslation(["content", "common"]);
  const [value, setValue] = useState(String(stack?.maxConcurrent ?? ""));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (stack) setValue(String(stack.maxConcurrent));
  }, [stack?.maxConcurrent]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!stack) return null;
  const n = Number(value);
  const valid = Number.isInteger(n) && n >= 1 && n <= 100;
  const dirty = value.trim() !== "" && n !== stack.maxConcurrent;

  // Applies on blur / Enter: a control that sits on the page saves itself.
  const save = async () => {
    if (!dirty || saving) return;
    if (!valid) {
      const msg = t("ingestOverviewPage.capacity.invalid");
      setErr(msg);
      toast.error(
        t("ingestOverviewPage.toasts.capInvalid", {
          stack: stack.label,
          reason: msg,
        })
      );
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      await foyerApi.setIngestStackLimit(stack.stack, n);
      toast.success(
        t("ingestOverviewPage.toasts.capSaved", {
          stack: stack.label,
          count: n,
        })
      );
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      toast.error(
        describeError(t, "setStackCapacity", e, { stack: stack.label })
      );
      setValue(String(stack.maxConcurrent));
    } finally {
      setSaving(false);
    }
  };

  return (
    <EditorCard eyebrow={t("ingestOverviewPage.capacity.eyebrow")}>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
        <Box>
          <Typography variant="body2" sx={{ fontWeight: "fontWeightSemibold" }}>
            {stack.label}{" "}
            <Typography
              component="span"
              variant="caption"
              color="text.secondary"
            >
              ({stack.stack})
            </Typography>
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t("ingestOverviewPage.capacity.usage", {
              transcoding: stack.transcoding,
              max: stack.maxConcurrent,
            })}
          </Typography>
        </Box>
        <TextField
          size="small"
          type="number"
          label={t("ingestOverviewPage.capacity.label")}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={() => void save()}
          onKeyDown={(e) => {
            if (e.key === "Enter") void save();
          }}
          disabled={saving}
          inputProps={{ min: 1, max: 100, "data-testid": "stack-cap-input" }}
          sx={{ width: 140 }}
        />
      </Stack>
      {err && (
        <Alert severity="error" sx={{ mt: 1, typography: "meta" }}>
          {err}
        </Alert>
      )}
    </EditorCard>
  );
}

export default function IngestOverviewPage() {
  const { t } = useTranslation("content");
  const [view, setView] = useState<IngestOverviewView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [bucket, setBucket] = useState<IngestBucket | "all">("all");
  const [source, setSource] = useState<IngestSource | "all">("all");

  const load = useCallback(async () => {
    try {
      setView(
        await foyerApi.ingestOverview({
          bucket: bucket === "all" ? undefined : bucket,
          source: source === "all" ? undefined : source,
        })
      );
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [bucket, source]);

  useEffect(() => {
    void load();
    const timer = setInterval(() => void load(), POLL_MS);
    return () => clearInterval(timer);
  }, [load]);

  return (
    <PageContainer>
      <PageHeader
        eyebrow={t("ingestOverviewPage.eyebrow")}
        title={t("ingestOverviewPage.title")}
        subtitle={t("ingestOverviewPage.subtitle")}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2, typography: "meta" }}>
          {error}
        </Alert>
      )}
      {view && !view.readiness.ready && (
        <Alert
          severity="warning"
          sx={{ mb: 2, typography: "meta" }}
          data-testid="ingest-not-ready"
        >
          {view.readiness.explanation}
        </Alert>
      )}

      {view === null && !error && (
        <Stack alignItems="center" sx={{ py: 4 }}>
          <CircularProgress size={24} />
        </Stack>
      )}

      {view && (
        <Stack spacing={2}>
          <StackCap view={view} onChanged={load} />

          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
            {INGEST_SOURCES.map((s) => {
              const c = view.summary[s];
              const selected = source === s;
              return (
                <Box
                  key={s}
                  sx={{
                    flex: "1 1 200px",
                    cursor: "pointer",
                    borderRadius: "14px",
                    outline: selected ? `2px solid ${tokens.brand}` : "none",
                  }}
                  onClick={() => setSource(selected ? "all" : s)}
                  data-testid={`source-tile-${s}`}
                >
                  <EditorCard eyebrow={t(`ingestOverviewPage.source.${s}`)}>
                    <Stack
                      direction="row"
                      spacing={1.5}
                      flexWrap="wrap"
                      useFlexGap
                    >
                      {INGEST_BUCKETS.map((b) => (
                        <Typography
                          key={b}
                          variant="caption"
                          sx={{
                            color: c[b] > 0 ? BUCKET_TONE[b] : tokens.inkMuted,
                          }}
                          data-testid={`count-${s}-${b}`}
                        >
                          <strong>{c[b]}</strong>{" "}
                          {t(`ingestOverviewPage.bucketTile.${b}`)}
                        </Typography>
                      ))}
                    </Stack>
                  </EditorCard>
                </Box>
              );
            })}
          </Stack>

          <Tabs
            value={bucket}
            onChange={(_, v) => setBucket(v)}
            variant="scrollable"
            sx={{ minHeight: 36 }}
          >
            <Tab
              value="all"
              label={t("ingestOverviewPage.all")}
              data-testid="bucket-all"
            />
            {INGEST_BUCKETS.map((b) => (
              <Tab
                key={b}
                value={b}
                label={t(`ingestOverviewPage.bucket.${b}`)}
                data-testid={`bucket-${b}`}
              />
            ))}
          </Tabs>

          {view.jobs.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              data-testid="ingest-empty"
            >
              {t("ingestOverviewPage.empty")}
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("ingestOverviewPage.columns.title")}</TableCell>
                  <TableCell>
                    {t("ingestOverviewPage.columns.source")}
                  </TableCell>
                  <TableCell>{t("ingestOverviewPage.columns.farm")}</TableCell>
                  <TableCell>{t("ingestOverviewPage.columns.state")}</TableCell>
                  <TableCell>
                    {t("ingestOverviewPage.columns.inState")}
                  </TableCell>
                  <TableCell>
                    {t("ingestOverviewPage.columns.detail")}
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {view.jobs.map((job) => (
                  <Row key={job.id} job={job} view={view} onChanged={load} />
                ))}
              </TableBody>
            </Table>
          )}
        </Stack>
      )}
    </PageContainer>
  );
}
